import React from 'react';
import IconRenderer from '../../ui/IconRenderer';
import { ReactComponent as NavForwardIcon } from '../../../Icons/nav-forward.svg';
import { ReactComponent as LearnModeIcon } from '../../../Icons/learn-mode.svg';
import { ReactComponent as BookIcon } from '../../../Icons/book.svg';
import './ExploreGrid.css';

/**
 * Reusable Explore Card Component
 * 
 * @param {Object} props
 * @param {string} props.title - Card title
 * @param {string} props.description - Card description
 * @param {string} props.language - Language name for icon (optional)
 * @param {string} props.category - Category name (optional)
 * @param {Function} props.getCategoryDisplayName - Function to format category name (optional)
 * @param {string|ReactNode} props.icon - Icon type: 'language', 'svg', or ReactNode (optional)
 * @param {string} props.iconName - Icon name for IconRenderer when icon='language' (optional)
 * @param {string} props.iconSize - Icon size: 'small' or 'large' (default: 'large')
 * @param {ReactNode} props.rightContent - Content for right column (price, status indicator, etc.)
 * @param {Array} props.badges - Array of badge objects: { text, className } (optional)
 * @param {boolean} props.badgesBelowDescription - Whether to place badges below description instead of in header (optional)
 * @param {Array} props.actions - Array of action button objects: { icon, onClick, className, ariaLabel, title, disabled, loading } (optional)
 * @param {Function} props.onClick - Click handler for center content (optional)
 * @param {string} props.className - Additional CSS classes (optional)
 * @param {boolean} props.isActive - Whether card is in active state (optional)
 * @param {ReactNode} props.children - Additional content to render (optional)
 */
const ExploreCard = ({
  title,
  description,
  language,
  category,
  getCategoryDisplayName,
  icon = 'language',
  iconName,
  iconSize = 'large',
  contentType,
  rightContent,
  badges = [],
  badgesBelowDescription = false,
  actions = [],
  onClick,
  className = '',
  isActive = false,
  children
}) => {
  // Determine icon to display
  // Priority: explicit icon prop > contentType
  const renderIcon = () => {
    // If icon is explicitly set to 'language', always use language icon
    if (icon === 'language' && (iconName || language)) {
      return (
        <IconRenderer
          name={(iconName || language).toLowerCase()}
          type="language"
          size={iconSize}
          className="explore-grid-card-icon"
        />
      );
    }

    // If icon is explicitly set to 'svg', use SVG
    if (icon === 'svg') {
      // Custom SVG icon passed as children or default
      return children || (
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" className="explore-grid-card-icon">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      );
    }

    // If icon is explicitly set to 'course' or 'cookbook', use those
    if (icon === 'course') {
      return (
        <LearnModeIcon
          className="explore-grid-card-icon"
          style={{ width: iconSize === 'small' ? '24px' : '32px', height: iconSize === 'small' ? '24px' : '32px' }}
        />
      );
    }

    if (icon === 'cookbook') {
      return (
        <BookIcon
          className="explore-grid-card-icon"
          style={{ width: iconSize === 'small' ? '24px' : '32px', height: iconSize === 'small' ? '24px' : '32px' }}
        />
      );
    }

    // Default: use language icon if language is available
    if (language) {
      return (
        <IconRenderer
          name={language.toLowerCase()}
          type="language"
          size={iconSize}
          className="explore-grid-card-icon"
        />
      );
    }

    return null;
  };

  const cardClasses = [
    'explore-grid-card',
    className,
    isActive ? 'category-card--active' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses} onClick={onClick}>
      {/* Left Column: Icon */}
      <div className="explore-grid-card-left">
        <div className="explore-grid-card-icon-wrapper">
          {renderIcon()}
        </div>
      </div>

      {/* Center Column: Content */}
      <div className="explore-grid-card-center" onClick={onClick}>
        <div className="explore-grid-card-header">
          <h4 className="explore-grid-card-title" title={title}>
            {title}
          </h4>
          {badges.length > 0 && !badgesBelowDescription && (
            <div className="explore-grid-card-badges">
              {badges.map((badge, index) => (
                <span
                  key={index}
                  className={badge.className || 'explore-grid-card-badge'}
                >
                  {badge.text}
                </span>
              ))}
            </div>
          )}
        </div>

        {category && (
          <div className="explore-grid-card-language-info">
            {contentType === 'course' ? (
              <LearnModeIcon className="explore-grid-card-info-icon" />
            ) : contentType === 'cookbook' ? (
              <BookIcon className="explore-grid-card-info-icon" />
            ) : (
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" className="explore-grid-card-info-icon">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            )}
            <span>{getCategoryDisplayName ? getCategoryDisplayName(category) : category}</span>
          </div>
        )}

        {description && (
          <p className="explore-grid-card-description" title={description}>
            {description}
          </p>
        )}

        {badges.length > 0 && badgesBelowDescription && (
          <div className="explore-grid-card-badges">
            {badges.map((badge, index) => (
              <span
                key={index}
                className={badge.className || 'explore-grid-card-badge'}
              >
                {badge.text}
              </span>
            ))}
          </div>
        )}

        {/* Only render children if not using them as SVG icon */}
        {icon !== 'svg' && children}
      </div>

      {/* Right Column: Price, Status, etc. */}
      {rightContent && (
        <div className="explore-grid-card-right">
          {rightContent}
        </div>
      )}

      {/* Action Icons - Bottom Right */}
      {actions.length > 0 && (
        <div className="explore-grid-card-actions">
          {actions.map((action, index) => (
            <button
              key={index}
              type="button"
              className={`explore-grid-card-action-icon ${action.className || ''}`}
              onClick={(e) => {
                e.stopPropagation();
                if (action.onClick) action.onClick();
              }}
              disabled={action.disabled}
              aria-label={action.ariaLabel || action.title}
              title={action.title}
            >
              {action.loading ? (
                <span className="spinner-small"></span>
              ) : (
                action.icon
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExploreCard;

